import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = '/api/software-library';
function auth() { return { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }; }

const CAT_META = {
  browser:       { icon: '🌐', color: '#1d4ed8', bg: '#dbeafe' },
  'dev-tools':   { icon: '🛠', color: '#d97706', bg: '#fef3c7' },
  office:        { icon: '📄', color: '#16a34a', bg: '#dcfce7' },
  utilities:     { icon: '⚙️',  color: '#7c3aed', bg: '#ede9fe' },
  security:      { icon: '🔒', color: '#dc2626', bg: '#fee2e2' },
  communication: { icon: '💬', color: '#0891b2', bg: '#cffafe' },
  media:         { icon: '🎬', color: '#db2777', bg: '#fce7f3' },
  other:         { icon: '📦', color: '#64748b', bg: '#f1f5f9' },
};

const STATUS_META = {
  pending:     { color: '#d97706', bg: '#fef3c7', label: '⏳ Pending Approval' },
  approved:    { color: '#1d4ed8', bg: '#dbeafe', label: '✅ Approved' },
  in_progress: { color: '#7c3aed', bg: '#ede9fe', label: '⟳ Installing'       },
  success:     { color: '#16a34a', bg: '#dcfce7', label: '✔ Installed'         },
  failed:      { color: '#dc2626', bg: '#fee2e2', label: '✘ Failed'            },
  rejected:    { color: '#64748b', bg: '#f1f5f9', label: '✘ Rejected'          },
};

function timeSince(d) {
  if (!d) return '—';
  const m = Math.floor((Date.now() - new Date(d)) / 60000);
  if (m < 60)   return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
}

export default function SoftwareLibrary({ user }) {
  const isAdmin = user?.role === 'admin';
  const [tab,       setTab]       = useState('catalog');
  const [library,   setLibrary]   = useState([]);
  const [requests,  setRequests]  = useState([]);
  const [devices,   setDevices]   = useState([]);
  const [summary,   setSummary]   = useState({});
  const [loading,   setLoading]   = useState(true);
  const [msg,       setMsg]       = useState({ text: '', type: '' });
  const [catFilter, setCatFilter] = useState('all');
  const [search,    setSearch]    = useState('');
  const [selSw,     setSelSw]     = useState(null);   // selected software for deploy modal
  const [selDevIds, setSelDevIds] = useState([]);
  const [deploying, setDeploying] = useState(false);
  const [showAdd,   setShowAdd]   = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [form,      setForm]      = useState({ name:'', vendor:'', category:'utilities', description:'', wingetId:'', command:'', version:'latest', requiresApproval:false });
  const [saving,    setSaving]    = useState(false);
  const [reqFilter, setReqFilter] = useState('all');

  function flash(text, type='success') {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text:'', type:'' }), 4000);
  }

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [libRes, reqRes, devRes, sumRes] = await Promise.all([
        axios.get(API, auth()),
        axios.get(`${API}/requests`, auth()),
        axios.get('/api/agents', auth()),
        axios.get(`${API}/summary`, auth()),
      ]);
      setLibrary(libRes.data);
      setRequests(reqRes.data.requests || []);
      setDevices(devRes.data || []);
      setSummary(sumRes.data);
    } catch (e) {
      flash('Failed to load: ' + (e.response?.data?.error || e.message), 'error');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── filtered catalog ──────────────────────────────────────────────────────
  const filtered = library.filter(sw => {
    const matchCat = catFilter === 'all' || sw.category === catFilter;
    const matchQ   = !search || [sw.name, sw.vendor, sw.description].join(' ').toLowerCase().includes(search.toLowerCase());
    return matchCat && matchQ;
  });

  const categories = [...new Set(library.map(sw => sw.category))].sort();

  // ── deploy modal handlers ─────────────────────────────────────────────────
  function openDeploy(sw) { setSelSw(sw); setSelDevIds([]); }
  function closeDeploy()  { setSelSw(null); setSelDevIds([]); }

  async function submitDeploy() {
    if (!selSw || selDevIds.length === 0) return;
    setDeploying(true);
    try {
      const res = await axios.post(`${API}/${selSw.id}/request`, { deviceIds: selDevIds }, auth());
      flash(`✔ ${res.data.software} install queued on ${res.data.total} device(s)${selSw.requiresApproval ? ' — awaiting admin approval' : ''}`);
      closeDeploy();
      loadAll();
    } catch (e) {
      flash(e.response?.data?.error || e.message, 'error');
    } finally { setDeploying(false); }
  }

  // ── add/edit handlers ─────────────────────────────────────────────────────
  function openAdd()       { setForm({ name:'', vendor:'', category:'utilities', description:'', wingetId:'', command:'', version:'latest', requiresApproval:false }); setEditEntry(null); setShowAdd(true); }
  function openEdit(sw)    { setForm({ ...sw }); setEditEntry(sw); setShowAdd(true); }
  function closeForm()     { setShowAdd(false); setEditEntry(null); }

  async function saveForm(e) {
    e.preventDefault();
    if (!form.command && !form.wingetId) return flash('command or wingetId is required', 'error');
    setSaving(true);
    try {
      const cmd = form.command || `winget install --id ${form.wingetId} -e --silent --accept-package-agreements --accept-source-agreements`;
      const payload = { ...form, command: cmd };
      if (editEntry) {
        await axios.put(`${API}/${editEntry.id}`, payload, auth());
        flash('Entry updated');
      } else {
        await axios.post(API, payload, auth());
        flash('Entry added to library');
      }
      closeForm();
      loadAll();
    } catch (e) { flash(e.response?.data?.error || e.message, 'error'); }
    finally { setSaving(false); }
  }

  async function toggleActive(sw) {
    try {
      await axios.put(`${API}/${sw.id}`, { active: !sw.active }, auth());
      loadAll();
    } catch (e) { flash(e.response?.data?.error || e.message, 'error'); }
  }

  // ── approval handlers ─────────────────────────────────────────────────────
  async function approveReq(id) {
    try { await axios.post(`${API}/requests/${id}/approve`, {}, auth()); flash('Request approved — agent will install on next poll'); loadAll(); }
    catch (e) { flash(e.response?.data?.error || e.message, 'error'); }
  }
  async function rejectReq(id) {
    try { await axios.post(`${API}/requests/${id}/reject`, {}, auth()); flash('Request rejected'); loadAll(); }
    catch (e) { flash(e.response?.data?.error || e.message, 'error'); }
  }

  const pendingApprovals = requests.filter(r => r.status === 'pending').length;
  const filteredReqs = requests.filter(r => reqFilter === 'all' || r.status === reqFilter);

  return (
    <div>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="page-header">
        <div>
          <div className="page-title">📦 Software Library</div>
          <div className="page-subtitle">Admin-curated apps · Silent install via agent (no UAC) · Request & approve workflow</div>
        </div>
        <div className="flex-gap">
          {isAdmin && <button className="btn btn-primary" onClick={openAdd}>+ Add Software</button>}
          <button className="refresh-btn" onClick={loadAll} disabled={loading}>↻ Refresh</button>
        </div>
      </div>

      {/* ── Alert ──────────────────────────────────────────────────────── */}
      {msg.text && (
        <div className={`alert ${msg.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 16 }}>{msg.text}</div>
      )}

      {/* ── KPI cards ──────────────────────────────────────────────────── */}
      <div className="cards-row" style={{ gridTemplateColumns: 'repeat(5,1fr)', marginBottom: 20 }}>
        {[
          { label: 'Apps in Library', value: summary.totalApps || 0,      cls: 'blue'   },
          { label: 'Pending Approval', value: summary.pending || 0,        cls: 'amber', alert: (summary.pending || 0) > 0 },
          { label: 'Installs Queued',  value: summary.approved || 0,       cls: 'cyan'   },
          { label: 'Successful',        value: summary.success || 0,        cls: 'green'  },
          { label: 'Failed',            value: summary.failed || 0,         cls: 'red',   alert: (summary.failed || 0) > 0 },
        ].map(c => (
          <div key={c.label} className={`card ${c.cls}`} style={{ outline: c.alert ? '2px solid currentColor' : undefined }}>
            <div className="card-label">{c.label}</div>
            <div className="card-value">{c.value}</div>
          </div>
        ))}
      </div>

      {/* ── Tabs ───────────────────────────────────────────────────────── */}
      <div style={{ display:'flex', borderBottom:'2px solid #e8ecf4', marginBottom:20 }}>
        {[
          { id:'catalog',  label:`📦 Software Catalog (${library.length})` },
          { id:'requests', label:`📋 Install Requests${pendingApprovals > 0 ? ` 🔴${pendingApprovals}` : ''}` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background:'none', border:'none', cursor:'pointer', padding:'10px 20px',
            fontSize:13, fontWeight:600,
            color: tab === t.id ? '#1a56db' : '#94a3b8',
            borderBottom: tab === t.id ? '2px solid #1a56db' : '2px solid transparent',
            marginBottom: -2
          }}>{t.label}</button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════
          TAB: CATALOG
      ════════════════════════════════════════════════════════════════ */}
      {tab === 'catalog' && (
        <>
          {/* Search + category filter */}
          <div className="flex-gap" style={{ marginBottom:16, flexWrap:'wrap' }}>
            <input className="form-input" style={{ maxWidth:280 }}
              placeholder="Search apps..." value={search} onChange={e => setSearch(e.target.value)} />
            <div className="flex-gap">
              {['all', ...categories].map(c => {
                const m = CAT_META[c] || {};
                return (
                  <button key={c}
                    onClick={() => setCatFilter(c)}
                    style={{
                      padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:600,
                      cursor:'pointer', border:'none',
                      background: catFilter === c ? (m.color || '#1a56db') : (m.bg || '#f1f5f9'),
                      color: catFilter === c ? '#fff' : (m.color || '#64748b'),
                    }}>
                    {c === 'all' ? `All (${library.length})` : `${m.icon||'📦'} ${c} (${library.filter(s=>s.category===c).length})`}
                  </button>
                );
              })}
            </div>
          </div>

          {loading ? <div className="loading">Loading library...</div> : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px,1fr))', gap:16 }}>
              {filtered.length === 0 && (
                <div className="empty" style={{ gridColumn:'1/-1', padding:48 }}>No software found.</div>
              )}
              {filtered.map(sw => {
                const m = CAT_META[sw.category] || CAT_META.other;
                return (
                  <div key={sw.id} style={{
                    background:'#fff', borderRadius:12, border:'1px solid #e8ecf4',
                    boxShadow:'0 1px 4px rgba(0,0,0,0.05)',
                    opacity: sw.active ? 1 : 0.5,
                    display:'flex', flexDirection:'column',
                  }}>
                    {/* Card header */}
                    <div style={{ padding:'16px 18px 10px', display:'flex', alignItems:'center', gap:12 }}>
                      <div style={{ width:44, height:44, borderRadius:10, background:m.bg,
                        display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>
                        {m.icon}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:700, fontSize:14, color:'#1e2a3b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{sw.name}</div>
                        <div style={{ fontSize:11, color:'#64748b' }}>{sw.vendor} · {sw.version}</div>
                      </div>
                      <span style={{ fontSize:10, fontWeight:700, background:m.bg, color:m.color,
                        padding:'2px 8px', borderRadius:12 }}>{sw.category}</span>
                    </div>

                    {/* Description */}
                    <div style={{ padding:'0 18px 10px', fontSize:12, color:'#64748b', flex:1 }}>
                      {sw.description || 'No description.'}
                    </div>

                    {/* Stats row */}
                    <div style={{ padding:'8px 18px', borderTop:'1px solid #f0f4ff',
                      display:'flex', gap:12, fontSize:11, color:'#94a3b8' }}>
                      {sw.stats?.success   > 0 && <span style={{color:'#16a34a'}}>✔ {sw.stats.success} installed</span>}
                      {sw.stats?.pending   > 0 && <span style={{color:'#d97706'}}>⏳ {sw.stats.pending} pending</span>}
                      {sw.stats?.failed    > 0 && <span style={{color:'#dc2626'}}>✘ {sw.stats.failed} failed</span>}
                      {!sw.stats?.success && !sw.stats?.pending && !sw.stats?.failed && <span>No installs yet</span>}
                      {sw.requiresApproval && <span style={{marginLeft:'auto', color:'#d97706'}}>🔐 Needs approval</span>}
                    </div>

                    {/* Actions */}
                    <div style={{ padding:'10px 18px', borderTop:'1px solid #f0f4ff', display:'flex', gap:8 }}>
                      <button className="btn btn-primary btn-sm" style={{ flex:1 }}
                        onClick={() => openDeploy(sw)} disabled={!sw.active}>
                        🚀 Deploy to Device
                      </button>
                      {isAdmin && (
                        <>
                          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(sw)}>✏</button>
                          <button className="btn btn-ghost btn-sm"
                            style={{ color: sw.active ? '#dc2626' : '#16a34a' }}
                            onClick={() => toggleActive(sw)}>
                            {sw.active ? '⊘' : '✔'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════
          TAB: REQUESTS
      ════════════════════════════════════════════════════════════════ */}
      {tab === 'requests' && (
        <>
          <div className="flex-gap" style={{ marginBottom:16 }}>
            {['all','pending','approved','in_progress','success','failed','rejected'].map(s => (
              <button key={s}
                className={`btn btn-sm ${reqFilter===s?'btn-primary':'btn-outline'}`}
                onClick={() => setReqFilter(s)}>
                {s === 'all' ? `All (${requests.length})` : `${STATUS_META[s]?.label||s} (${requests.filter(r=>r.status===s).length})`}
              </button>
            ))}
          </div>

          <div className="panel">
            <div className="panel-header">
              <div className="panel-title"><div className="panel-title-icon">📋</div>Install Requests</div>
            </div>
            {loading ? <div className="loading">Loading...</div> : (
              <table>
                <thead>
                  <tr>
                    <th>Software</th>
                    <th>Device</th>
                    <th>Requested By</th>
                    <th>Status</th>
                    <th>Requested</th>
                    <th>Completed</th>
                    {isAdmin && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredReqs.length === 0 && (
                    <tr><td colSpan={isAdmin?7:6} className="empty">No requests found.</td></tr>
                  )}
                  {filteredReqs.map(r => {
                    const sm = STATUS_META[r.status] || STATUS_META.pending;
                    const cat = r.software ? (CAT_META[r.software.category] || CAT_META.other) : CAT_META.other;
                    return (
                      <tr key={r.id}>
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontSize:16 }}>{cat.icon}</span>
                            <div>
                              <div style={{ fontWeight:600, fontSize:13 }}>{r.software?.name || '—'}</div>
                              <div style={{ fontSize:11, color:'#94a3b8' }}>{r.software?.vendor} · v{r.software?.version}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ fontSize:13, fontWeight:600 }}>{r.device?.hostname || '—'}</div>
                          <div style={{ fontSize:11, color:'#94a3b8' }}>{r.device?.ipAddress}</div>
                        </td>
                        <td style={{ fontSize:12 }}>{r.requestedBy}</td>
                        <td>
                          <span style={{ fontSize:11, fontWeight:700, padding:'2px 10px', borderRadius:20,
                            background:sm.bg, color:sm.color }}>{sm.label}</span>
                          {r.error && <div style={{ fontSize:10, color:'#dc2626', marginTop:2 }}>{r.error.slice(0,60)}</div>}
                        </td>
                        <td style={{ fontSize:11, color:'#94a3b8' }}>{timeSince(r.createdAt)}</td>
                        <td style={{ fontSize:11, color:'#94a3b8' }}>{r.completedAt ? timeSince(r.completedAt) : '—'}</td>
                        {isAdmin && (
                          <td>
                            {r.status === 'pending' && (
                              <div className="flex-gap">
                                <button className="btn btn-sm btn-primary" onClick={() => approveReq(r.id)}>✔ Approve</button>
                                <button className="btn btn-sm btn-ghost" style={{color:'#dc2626'}} onClick={() => rejectReq(r.id)}>✘ Reject</button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════
          DEPLOY MODAL
      ════════════════════════════════════════════════════════════════ */}
      {selSw && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex',
          alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:28, width:520,
            maxHeight:'85vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize:16, fontWeight:700, color:'#1e2a3b', marginBottom:4 }}>
              🚀 Deploy — {selSw.name}
            </div>
            <div style={{ fontSize:12, color:'#94a3b8', marginBottom:20 }}>
              {selSw.vendor} · {selSw.version} · Installs silently via agent (no UAC prompt)
            </div>

            {selSw.requiresApproval && (
              <div className="alert info" style={{ marginBottom:16 }}>
                🔐 <strong>Requires admin approval</strong> before the agent installs.
              </div>
            )}

            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>Select Target Devices</div>
              <div style={{ border:'1px solid #e8ecf4', borderRadius:8, maxHeight:220, overflowY:'auto' }}>
                {devices.filter(d => d.status === 'online' || d.status).map(d => (
                  <label key={d.id} style={{ display:'flex', alignItems:'center', gap:10,
                    padding:'9px 14px', cursor:'pointer', borderBottom:'1px solid #f0f4ff',
                    background: selDevIds.includes(d.id) ? '#eff4ff' : '#fff' }}>
                    <input type="checkbox" checked={selDevIds.includes(d.id)}
                      onChange={e => setSelDevIds(prev =>
                        e.target.checked ? [...prev, d.id] : prev.filter(x => x !== d.id)
                      )} />
                    <div>
                      <div style={{ fontSize:13, fontWeight:600 }}>{d.hostname}</div>
                      <div style={{ fontSize:11, color:'#94a3b8' }}>{d.os} · {d.ipAddress}</div>
                    </div>
                    <span className={`badge ${d.status==='online'?'online':'offline'}`} style={{ marginLeft:'auto', fontSize:10 }}>
                      {d.status==='online'?'● Online':'○ Offline'}
                    </span>
                  </label>
                ))}
              </div>
              <div style={{ fontSize:11, color:'#94a3b8', marginTop:6 }}>
                {selDevIds.length} device(s) selected
              </div>
            </div>

            <div className="flex-gap" style={{ justifyContent:'flex-end' }}>
              <button className="btn btn-ghost" onClick={closeDeploy}>Cancel</button>
              <button className="btn btn-primary" onClick={submitDeploy}
                disabled={deploying || selDevIds.length === 0}>
                {deploying ? '⟳ Queuing...' : `🚀 Deploy to ${selDevIds.length} Device(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════
          ADD / EDIT MODAL
      ════════════════════════════════════════════════════════════════ */}
      {showAdd && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex',
          alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:28, width:540,
            maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize:16, fontWeight:700, color:'#1e2a3b', marginBottom:20 }}>
              {editEntry ? '✏ Edit Software Entry' : '+ Add Software to Library'}
            </div>
            <form onSubmit={saveForm}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Name *</label>
                  <input className="form-input" value={form.name} required
                    onChange={e => setForm(f=>({...f, name:e.target.value}))} placeholder="e.g. Google Chrome" />
                </div>
                <div className="form-group">
                  <label className="form-label">Vendor</label>
                  <input className="form-input" value={form.vendor}
                    onChange={e => setForm(f=>({...f, vendor:e.target.value}))} placeholder="e.g. Google" />
                </div>
                <div className="form-group">
                  <label className="form-label">Version</label>
                  <input className="form-input" value={form.version}
                    onChange={e => setForm(f=>({...f, version:e.target.value}))} placeholder="latest" />
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="form-input" value={form.category}
                    onChange={e => setForm(f=>({...f, category:e.target.value}))}>
                    {Object.keys(CAT_META).map(c => <option key={c} value={c}>{CAT_META[c].icon} {c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Winget ID (optional)</label>
                  <input className="form-input" value={form.wingetId||''}
                    onChange={e => setForm(f=>({...f, wingetId:e.target.value}))} placeholder="e.g. Google.Chrome" />
                </div>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Install Command *</label>
                  <textarea className="form-input" rows={3} value={form.command||''}
                    onChange={e => setForm(f=>({...f, command:e.target.value}))}
                    placeholder='winget install --id Google.Chrome -e --silent&#10;or full MSI/EXE command' />
                  <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>
                    If Winget ID is filled and command is empty, the winget command will be auto-built.
                  </div>
                </div>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Description</label>
                  <input className="form-input" value={form.description||''}
                    onChange={e => setForm(f=>({...f, description:e.target.value}))} placeholder="Short description shown to users" />
                </div>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13 }}>
                    <input type="checkbox" checked={!!form.requiresApproval}
                      onChange={e => setForm(f=>({...f, requiresApproval:e.target.checked}))} />
                    Require admin approval before agent installs
                  </label>
                </div>
              </div>
              <div className="flex-gap" style={{ justifyContent:'flex-end', marginTop:16 }}>
                <button type="button" className="btn btn-ghost" onClick={closeForm}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? '⟳ Saving...' : (editEntry ? 'Save Changes' : 'Add to Library')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
